import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type { ColorName } from '@/lib/colors'
import { supabase, describeError, type Row } from '@/lib/supabase'

export type Label = Omit<Row<'labels'>, 'color'> & { color: ColorName }

const labelKeys = { all: ['labels'] as const, forJob: (jobId: string) => ['job-labels', jobId] as const }

export function useLabels() {
  return useQuery({
    queryKey: labelKeys.all,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<Label[]> => {
      const { data, error } = await supabase.from('labels').select('*').order('sort_order').order('created_at')
      if (error) throw error
      return data as Label[]
    },
  })
}

export function useJobLabelIds(jobId: string) {
  return useQuery({
    queryKey: labelKeys.forJob(jobId),
    queryFn: async (): Promise<string[]> => {
      const { data, error } = await supabase.from('job_labels').select('label_id').eq('job_id', jobId)
      if (error) throw error
      return data.map((row) => row.label_id)
    },
  })
}

function useInvalidate() {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: labelKeys.all })
    void queryClient.invalidateQueries({ queryKey: ['job-labels'] })
    void queryClient.invalidateQueries({ queryKey: ['pipeline-board'] })
  }
}

export function useToggleJobLabel(jobId: string) {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async ({ labelId, on }: { labelId: string; on: boolean }) => {
      const { error } = on
        ? await supabase.from('job_labels').insert({ job_id: jobId, label_id: labelId })
        : await supabase.from('job_labels').delete().eq('job_id', jobId).eq('label_id', labelId)
      if (error) throw error
    },
    onSuccess: invalidate,
    onError: (error) => toast.error('Could not change the label', { description: describeError(error) }),
  })
}

export function useSaveLabel() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async (label: { id?: string; name: string | null; color: ColorName; sort_order?: number }) => {
      const { id, ...rest } = label
      const { error } = id ? await supabase.from('labels').update(rest).eq('id', id) : await supabase.from('labels').insert(rest)
      if (error) throw error
    },
    onSuccess: invalidate,
    onError: (error) => toast.error('Could not save the label', { description: describeError(error) }),
  })
}

export function useDeleteLabel() {
  const invalidate = useInvalidate()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('labels').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: invalidate,
    onError: (error) => toast.error('Could not delete the label', { description: describeError(error) }),
  })
}
